/** Default empty form — empty string / false means "leave unchanged" on apply. */
export const EMPTY_BG_SCAN_BULK_FORM = {
  bgScanOnOff: '',
  chanScanInterval: '',
  fastRoamingOnOff: '',
  scanInterval: '',
  selfImprovement: '',
  channels5GHz: '',
  channels6GHz: '',
  clearChannelGroups: false,
};

export function formatBgScanChannelList(channels) {
  if (!Array.isArray(channels) || channels.length === 0) return '';
  return channels.join(', ');
}

export function parseBgScanChannelList(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return [];
  const parts = trimmed.split(/[,;\s]+/).filter(Boolean);
  const out = [];
  for (const part of parts) {
    const n = parseInt(part, 10);
    if (Number.isNaN(n) || n < 0) {
      return { error: `"${part}" is not a valid channel number` };
    }
    out.push(n);
  }
  return { channels: out };
}

export function hasBgScanBulkFormChanges(form) {
  if (!form) return false;
  return !!(
    form.bgScanOnOff ||
    form.chanScanInterval.trim() ||
    form.fastRoamingOnOff ||
    form.scanInterval.trim() ||
    form.selfImprovement.trim() ||
    form.channels5GHz.trim() ||
    form.channels6GHz.trim() ||
    form.clearChannelGroups
  );
}

function parseOptionalInt(raw, label, { allowZero = true } = {}) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { value: undefined };
  const n = parseInt(trimmed, 10);
  if (Number.isNaN(n)) {
    return { error: `${label} must be a valid integer` };
  }
  if (!allowZero && n <= 0) {
    return { error: `${label} must be greater than 0` };
  }
  if (allowZero && n < 0) {
    return { error: `${label} must be 0 or greater` };
  }
  return { value: n };
}

/**
 * Build a BackgroundStaScanPatch object for PATCH /tree-mgmt/devices/config.
 * Returns { patch, errors } — patch is null when nothing to apply or on validation error.
 */
export function buildBackgroundStaScanBulkPatch(form) {
  const errors = [];
  const bg = {};

  if (form.bgScanOnOff === 'on' || form.bgScanOnOff === 'off') {
    bg.bg_scan_on_off = form.bgScanOnOff;
  }

  if (form.fastRoamingOnOff === 'on' || form.fastRoamingOnOff === 'off') {
    bg.fr_on_off = form.fastRoamingOnOff;
  }

  const chanScan = parseOptionalInt(form.chanScanInterval, 'Channel scan interval');
  if (chanScan.error) errors.push(chanScan.error);
  else if (chanScan.value !== undefined) bg.chan_scan_interval = chanScan.value;

  const scanInt = parseOptionalInt(form.scanInterval, 'Scan interval');
  if (scanInt.error) errors.push(scanInt.error);
  else if (scanInt.value !== undefined) bg.scan_interval = scanInt.value;

  const selfImp = parseOptionalInt(form.selfImprovement, 'Self improvement');
  if (selfImp.error) errors.push(selfImp.error);
  else if (selfImp.value !== undefined) bg.self_improvment = selfImp.value;

  const channelGroups = {};
  if (form.channels5GHz.trim()) {
    const parsed = parseBgScanChannelList(form.channels5GHz);
    if (parsed.error) errors.push(`5GHz channels: ${parsed.error}`);
    else channelGroups['5GHz'] = parsed.channels;
  }
  if (form.channels6GHz.trim()) {
    const parsed = parseBgScanChannelList(form.channels6GHz);
    if (parsed.error) errors.push(`6GHz channels: ${parsed.error}`);
    else channelGroups['6GHz'] = parsed.channels;
  }
  if (Object.keys(channelGroups).length > 0) {
    bg.channel_groups = channelGroups;
  }

  if (form.clearChannelGroups) {
    bg.clear_channel_groups = true;
  }

  if (errors.length > 0) {
    return { patch: null, errors };
  }
  if (Object.keys(bg).length === 0) {
    return { patch: null, errors: ['Provide at least one background scan field to update'] };
  }

  return {
    patch: {
      trees: [{ background_sta_scan: bg }],
    },
    errors: [],
  };
}
